import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { FaCheck } from "react-icons/fa";
import { HexColorPicker } from "react-colorful";
import {
  fetchSetupInfo,
  uploadSetupLogo,
  saveSetup,
  type WhiteLabelSetupInfo,
} from "../services/personalizationService";

const PRESET_COLORS = [
  "#0068FE",
  "#2563EB",
  "#4F46E5",
  "#7C3AED",
  "#DB2777",
  "#E11D48",
  "#EA580C",
  "#F59E0B",
  "#16A34A",
  "#0D9488",
  "#0891B2",
  "#111827",
];

const normalizeHex = (raw: string): string | null => {
  const v = raw.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v.toUpperCase()}`;
  return null;
};

const PersonalizationPage = () => {
  const { token } = useParams<{ token: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const [name, setName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#0068FE");
  const [hexDraft, setHexDraft] = useState("0068FE");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Garde le champ hexadécimal synchronisé quand la couleur change (pastille / picker)
  useEffect(() => {
    setHexDraft(primaryColor.replace("#", "").toUpperCase());
  }, [primaryColor]);

  // Ferme le color picker au clic en dehors
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [pickerOpen]);

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    // Mode démo local (sans backend) : /personalization/demo
    if (token === "demo") {
      const mock: WhiteLabelSetupInfo = {
        opticianName: "Optique Durand",
        name: "Optique Durand",
        primaryColor: "#0068FE",
        logoUrl: null,
        completed: false,
      };
      setName(mock.name);
      setPrimaryColor(mock.primaryColor);
      setLoading(false);
      return;
    }
    fetchSetupInfo(token)
      .then((data) => {
        setName(data.name || data.opticianName || "");
        setPrimaryColor(data.primaryColor || "#0068FE");
        setLogoPreview(data.logoUrl);
        if (data.completed) setDone(true);
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const onPickLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop lourde (max 5 Mo)");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (name.trim().length < 2) {
      toast.error("Indiquez le nom de votre application");
      return;
    }
    if (!logoPreview) {
      toast.error("Ajoutez votre logo");
      return;
    }
    setSubmitting(true);
    try {
      if (token === "demo") {
        await new Promise((r) => setTimeout(r, 500));
        setDone(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (logoFile) {
        await uploadSetupLogo(token, logoFile);
      }
      await saveSetup(token, { name: name.trim(), primaryColor });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-[#5e6075] text-sm">Chargement…</div>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-[#12141d]">Lien invalide</h1>
          <p className="text-[#5e6075] mt-2 text-sm">
            Ce lien de personnalisation n'est plus valide ou le paiement n'a pas été finalisé.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Outfit',system-ui,sans-serif]">
      <Toaster position="top-center" />
      <header className="border-b border-[#E5E7EB] px-6 py-5">
        <span className="text-[#0068FE] font-bold text-lg">Fideliz</span>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        {done ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#E8F8EF] flex items-center justify-center mx-auto mb-6 text-3xl">
              🎉
            </div>
            <h1 className="text-2xl font-bold text-[#12141d]">Votre application est en préparation</h1>
            <p className="text-[#5e6075] mt-3">
              Merci ! Nous avons bien reçu votre logo, le nom <strong>{name}</strong> et votre couleur.
              Notre équipe lance la fabrication de votre application. Vous serez prévenu dès qu'elle
              sera disponible sur les stores.
            </p>
            <a
              href="/login"
              className="inline-block mt-8 bg-[#0068FE] text-white rounded-lg font-medium py-3 px-6 hover:bg-[#0052CC] transition-colors"
            >
              Accéder à mon espace
            </a>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <p className="text-[#0068FE] text-xs font-semibold uppercase tracking-wide mb-2">
                Marque blanche
              </p>
              <h1 className="text-2xl font-bold text-[#12141d]">Personnalisez votre application</h1>
              <p className="text-[#5e6075] mt-2">
                Paiement confirmé ✓ — dernière étape : indiquez le logo, le nom et la couleur de votre
                application. Notre équipe la fabrique ensuite.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-[#12141d] mb-2">Votre logo *</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-2xl border border-[#E5E7EB] bg-[#f6f6f9] flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="logo" className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-[#94a3b8] text-xs">Aperçu</span>
                    )}
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={onPickLogo}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="border border-[#E5E7EB] rounded-lg px-4 py-2 text-sm font-medium text-[#12141d] hover:bg-[#f6f6f9]"
                    >
                      {logoPreview ? "Changer le logo" : "Choisir une image"}
                    </button>
                    <p className="text-xs text-[#94a3b8] mt-1">PNG, JPG ou WEBP — 5 Mo max.</p>
                  </div>
                </div>
              </div>

              {/* Nom de l'app */}
              <div>
                <label className="block text-sm font-medium text-[#12141d] mb-2">
                  Nom de l'application *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={30}
                  className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 focus:border-[#12141d] focus:outline-none"
                  placeholder="Ex: Optique Durand"
                />
                <p className="text-xs text-[#94a3b8] mt-1">
                  C'est le nom affiché sous l'icône de l'app (30 caractères max).
                </p>
              </div>

              {/* Couleur principale */}
              <div>
                <label className="block text-sm font-medium text-[#12141d] mb-3">
                  Couleur principale *
                </label>

                <div className="rounded-2xl border border-[#E5E7EB] p-5">
                  {/* Pastilles prédéfinies */}
                  <div className="flex flex-wrap gap-2.5">
                    {PRESET_COLORS.map((c) => {
                      const selected = primaryColor.toUpperCase() === c.toUpperCase();
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setPrimaryColor(c)}
                          aria-label={c}
                          className={`relative w-9 h-9 rounded-full transition-all duration-150 hover:scale-110 ${
                            selected
                              ? "ring-2 ring-offset-2 ring-[#12141d] scale-110"
                              : "ring-1 ring-black/5"
                          }`}
                          style={{ backgroundColor: c }}
                        >
                          {selected && (
                            <FaCheck className="w-3.5 h-3.5 text-white absolute inset-0 m-auto drop-shadow" />
                          )}
                        </button>
                      );
                    })}

                    {/* Couleur personnalisée — popover custom (react-colorful) */}
                    <div className="relative" ref={pickerRef}>
                      <button
                        type="button"
                        onClick={() => setPickerOpen((o) => !o)}
                        className={`relative w-9 h-9 rounded-full cursor-pointer hover:scale-110 transition-transform flex items-center justify-center ${
                          pickerOpen ? "ring-2 ring-offset-2 ring-[#12141d]" : "ring-1 ring-black/10"
                        }`}
                        style={{
                          background:
                            "conic-gradient(from 90deg, #ff0000, #ffea00, #00ff37, #00fff2, #0040ff, #d600ff, #ff0000)",
                        }}
                        title="Couleur personnalisée"
                      >
                        <span className="w-3.5 h-3.5 rounded-full bg-white/90 shadow-inner" />
                      </button>

                      {pickerOpen && (
                        <div className="absolute z-30 mt-2 left-0 bg-white rounded-2xl shadow-2xl border border-[#E5E7EB] p-3 w-60 animate-[fadeIn_120ms_ease-out]">
                          <style>{`
                            .fdz-picker .react-colorful { width: 100%; height: 170px; }
                            .fdz-picker .react-colorful__saturation { border-radius: 12px 12px 0 0; }
                            .fdz-picker .react-colorful__last-control { border-radius: 0 0 12px 12px; height: 16px; margin-top: 12px; }
                            .fdz-picker .react-colorful__hue { border-radius: 10px; }
                            .fdz-picker .react-colorful__pointer { width: 18px; height: 18px; }
                          `}</style>
                          <div className="fdz-picker">
                            <HexColorPicker
                              color={primaryColor}
                              onChange={(c) => setPrimaryColor(c.toUpperCase())}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-3 gap-2">
                            <div
                              className="w-9 h-9 rounded-lg ring-1 ring-black/5 shrink-0"
                              style={{ backgroundColor: primaryColor }}
                            />
                            <div className="flex items-center rounded-lg border border-[#E5E7EB] px-2.5 h-9 flex-1 focus-within:border-[#12141d]">
                              <span className="text-[#94a3b8] font-medium text-sm">#</span>
                              <input
                                type="text"
                                value={hexDraft}
                                onChange={(e) => {
                                  const v = e.target.value
                                    .replace(/[^0-9a-fA-F]/g, "")
                                    .slice(0, 6)
                                    .toUpperCase();
                                  setHexDraft(v);
                                  const norm = normalizeHex(v);
                                  if (norm) setPrimaryColor(norm);
                                }}
                                className="w-full bg-transparent outline-none font-mono uppercase text-sm text-[#12141d]"
                                placeholder="0068FE"
                                maxLength={6}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Champ hex + aperçu */}
                  <div className="flex items-center gap-3 mt-5">
                    <div
                      className="w-12 h-12 rounded-xl shadow-sm ring-1 ring-black/5 shrink-0"
                      style={{ backgroundColor: primaryColor }}
                    />
                    <div className="flex items-center rounded-lg border border-[#E5E7EB] px-3 h-12 focus-within:border-[#12141d] transition-colors">
                      <span className="text-[#94a3b8] font-medium">#</span>
                      <input
                        type="text"
                        value={hexDraft}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();
                          setHexDraft(v);
                          const norm = normalizeHex(v);
                          if (norm) setPrimaryColor(norm);
                        }}
                        className="w-24 bg-transparent outline-none font-mono uppercase tracking-wide text-[#12141d]"
                        placeholder="0068FE"
                        maxLength={6}
                      />
                    </div>

                    {/* Mini aperçu d'app en direct */}
                    <div className="ml-auto rounded-xl border border-[#E5E7EB] overflow-hidden w-28 shrink-0">
                      <div className="h-7 flex items-center px-2 gap-1.5" style={{ backgroundColor: primaryColor }}>
                        {logoPreview ? (
                          <img src={logoPreview} alt="" className="w-4 h-4 rounded object-contain bg-white/80" />
                        ) : (
                          <span className="w-4 h-4 rounded bg-white/40" />
                        )}
                        <span className="text-white text-[9px] font-semibold truncate">
                          {name || "Mon app"}
                        </span>
                      </div>
                      <div className="p-2 bg-white">
                        <div className="h-1.5 rounded-full bg-[#EEF0F4] mb-1.5" />
                        <div className="h-1.5 w-3/4 rounded-full bg-[#EEF0F4]" />
                        <div
                          className="mt-2 h-5 rounded-md text-white text-[8px] font-semibold flex items-center justify-center"
                          style={{ backgroundColor: primaryColor }}
                        >
                          Bouton
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-[#94a3b8] mt-2">
                  Couleur des boutons et de l'identité de votre app.
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="bg-[#0068FE] text-white rounded-lg font-medium py-4 w-full hover:bg-[#0052CC] transition-colors disabled:opacity-50"
              >
                {submitting ? "Enregistrement…" : "Valider et lancer la fabrication"}
              </button>
            </form>
          </>
        )}

        <footer className="text-xs text-[#94a3b8] mt-12 text-center">
          Une question ? contact@fideliz.fr
        </footer>
      </main>
    </div>
  );
};

export default PersonalizationPage;
